import { FC } from "react";
import { useTranslation } from "react-i18next";
import {NearbyIcon} from "src/assets/Icons";

interface LocationInfoProps {
  attractions: string[];
  latitude: number | null;
  longitude: number | null;
}

const stripIndexPrefix = (entry: string) => entry.replace(/^\s*\d+\)\s*:\s*/, "").trim();

const LocationInfo: FC<LocationInfoProps> = ({ attractions, latitude, longitude }) => {
  const { t } = useTranslation();
  const hasMap = latitude != null && longitude != null;
  const attractionNames = (attractions ?? []).map(stripIndexPrefix).filter(Boolean);
  const hasAttractions = attractionNames.length > 0;

  if (!hasMap && !hasAttractions) return null;

  return (
    <div>
      <h3 className="mb-6 text-lg font-semibold text-font-default">{t("Hotels.booking.locationAndAreaInfo")}</h3>

      <div className="relative mb-8 flex h-64 w-full items-center justify-center overflow-hidden rounded-lg bg-background-subtle">
        {hasMap ? (
          <iframe
            src={`https://maps.google.com/maps?q=${latitude},${longitude}&z=14&output=embed`}
            title={t("Hotels.booking.hotelLocationMap")}
            width="100%"
            height="100%"
            frameBorder="0"
            style={{ border: 0 }}
            allowFullScreen
            aria-hidden="false"
            tabIndex={0}
            className="absolute inset-0 z-0 grayscale"
          />
        ) : (
          <span className="text-sm text-font-minimal">{t("Hotels.booking.mapPreview")}</span>
        )}
      </div>

      {hasAttractions && (
        <div className="text-sm">
          <h4 className="mb-4 flex items-center gap-2 font-bold text-font-default">
            <NearbyIcon className="h-4.5 w-4.5 text-icon-active" />
            {t("Hotels.booking.whatsNearby")}
          </h4>
          <ul className="grid grid-cols-1 gap-x-12 sm:grid-cols-2 md:grid-cols-3">
            {attractionNames.map((name, idx) => (
              <li key={`${idx}-${name}`} className="flex items-start justify-between gap-4 border-b border-border-default py-3 last:border-b-0">
                <span className="text-font-subtle">{name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LocationInfo;
